import { Link } from 'react-router-dom';
import { Mail, Phone, MessageCircle, HelpCircle } from 'lucide-react';
import { useState } from 'react';
import { subscribeNewsletter } from '../utils/api';
import { toast } from 'sonner';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      const result = await subscribeNewsletter(email);
      if (result.success) {
        toast.success(result.message);
        setEmail('');
      }
    } catch (error) {
      toast.error('Subscription failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="bg-[#0F2A4D]">
      {/* Newsletter Section */}
      <div className="border-b border-[#1F3C88]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">
                Subscribe to Intelligence Briefings
              </h3>
              <p className="text-slate-300">
                Receive exclusive market insights and analysis directly to your inbox
              </p>
            </div>
            <form onSubmit={handleSubscribe} className="flex gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-[#1F3C88] border border-[#007BFF]/50 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#007BFF] focus:border-transparent"
                required
              />
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-3 bg-[#007BFF] text-white font-medium rounded-md hover:bg-[#0066DD] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isLoading ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          
          {/* Company Logo & Description */}
          <div className="lg:pr-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_22bcd9cf-3cc7-43c2-99a5-951906bdc6ea/artifacts/m62khqfb_2.png" 
              alt="Enerlligence Analytics" 
              className="h-14 w-auto mb-6"
            />
            <p className="text-sm text-white leading-relaxed mb-6">
              Independent global energy and mining intelligence delivering actionable insights to industry leaders, investors, and policymakers worldwide.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-slate-400 hover:text-white transition-colors" aria-label="Twitter">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="#" className="text-slate-400 hover:text-white transition-colors" aria-label="LinkedIn">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="#" className="text-slate-400 hover:text-white transition-colors" aria-label="YouTube">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
            </div>
          </div>

          {/* About Enerlligence */}
          <div>
            <h4 className="text-white font-semibold text-base mb-6 tracking-wide">About Enerlligence</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-white hover:text-[#007BFF] transition-colors text-sm">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/about/story" className="text-white hover:text-[#007BFF] transition-colors text-sm">
                  Our Story
                </Link>
              </li>
              <li>
                <Link to="/about/people" className="text-white hover:text-[#007BFF] transition-colors text-sm">
                  Our People
                </Link>
              </li>
              <li>
                <Link to="/about/values" className="text-white hover:text-[#007BFF] transition-colors text-sm">
                  Our Values
                </Link>
              </li>
              <li>
                <Link to="/about/culture" className="text-white hover:text-[#007BFF] transition-colors text-sm">
                  Our Culture
                </Link>
              </li>
              <li>
                <Link to="/about/customers" className="text-white hover:text-[#007BFF] transition-colors text-sm">
                  Our Customers
                </Link>
              </li>
              <li>
                <Link to="/about/locations" className="text-white hover:text-[#007BFF] transition-colors text-sm">
                  Our Locations
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold text-base mb-6 tracking-wide">Services</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/commodities" className="text-white hover:text-[#007BFF] transition-colors text-sm">
                  Commodity Research
                </Link>
              </li>
              <li>
                <Link to="/countries" className="text-white hover:text-[#007BFF] transition-colors text-sm">
                  Country Analysis
                </Link>
              </li>
              <li>
                <Link to="/insights" className="text-white hover:text-[#007BFF] transition-colors text-sm">
                  Market Intelligence
                </Link>
              </li>
              <li>
                <Link to="/emissions" className="text-white hover:text-[#007BFF] transition-colors text-sm">
                  Emissions Tracking
                </Link>
              </li>
              <li>
                <Link to="/reports" className="text-white hover:text-[#007BFF] transition-colors text-sm">
                  Specialized Reports
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Us */}
          <div>
            <h4 className="text-white font-semibold text-base mb-6 tracking-wide">Contact Us</h4>
            <ul className="space-y-3">
              <li>
                <a href="mailto:info@enerlligence.com" className="text-white hover:text-[#007BFF] transition-colors text-sm flex items-center gap-2">
                  <Mail size={16} className="text-white flex-shrink-0" />
                  info@enerlligence.com
                </a>
              </li>
              <li>
                <Link to="/contact" className="text-white hover:text-[#007BFF] transition-colors text-sm flex items-center gap-2">
                  <Phone size={16} className="text-white flex-shrink-0" />
                  Customer Support
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-white hover:text-[#007BFF] transition-colors text-sm flex items-center gap-2">
                  <HelpCircle size={16} className="text-white flex-shrink-0" />
                  Help Centre
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-white hover:text-[#007BFF] transition-colors text-sm flex items-center gap-2">
                  <MessageCircle size={16} className="text-white flex-shrink-0" />
                  Speak to Enerlligence
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#1F3C88]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
            <p className="text-sm text-white text-center lg:text-left">
              © 2026 Enerlligence Analytics is part of the CrimsonGreen Business. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center lg:justify-end gap-x-6 gap-y-2 text-sm">
              <Link to="/disclaimer" className="text-white hover:text-[#007BFF] transition-colors">
                Disclaimer
              </Link>
              <Link to="/terms-of-service" className="text-white hover:text-[#007BFF] transition-colors">
                Terms of Service
              </Link>
              <Link to="/privacy-policy" className="text-white hover:text-[#007BFF] transition-colors">
                Privacy Policy
              </Link>
              <Link to="/cookie-policy" className="text-white hover:text-[#007BFF] transition-colors">
                Cookie Policy
              </Link>
              <Link to="/anti-slavery-policy" className="text-white hover:text-[#007BFF] transition-colors">
                Anti-Slavery Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
